import { defineStore } from "pinia";
import { ref } from "vue";
import type { Toast, ToastType, ToastOptions } from "@/types/toast";

export const useToastStore = defineStore("toast", () => {
  // State
  const toasts = ref<Toast[]>([]);
  const nextId = ref(0);

  // Actions
  function addToast(type: ToastType, options: ToastOptions): string {
    const id = `toast-${nextId.value++}`;
    const timeout = options.timeout ?? getDefaultTimeout(type);

    const toast: Toast = {
      id,
      type,
      message: options.message,
      title: options.title,
      emoji: options.emoji,
      timeout,
      click: options.click,
      createdAt: Date.now(),
    };

    toasts.value.push(toast);

    // Auto-remove after timeout
    if (timeout > 0) {
      setTimeout(() => {
        removeToast(id);
      }, timeout);
    }

    return id;
  }

  function removeToast(id: string): void {
    const index = toasts.value.findIndex((t) => t.id === id);
    if (index !== -1) {
      toasts.value.splice(index, 1);
    }
  }

  function clearAll(): void {
    toasts.value = [];
  }

  function reset(): void {
    toasts.value = [];
    nextId.value = 0;
  }

  function getDefaultTimeout(type: ToastType): number {
    switch (type) {
      case "error":
        return 6000;
      case "warning":
        return 4000;
      case "success":
      case "info":
      default:
        return 3000;
    }
  }

  return {
    toasts,
    addToast,
    removeToast,
    clearAll,
    reset,
  };
});
